import { useState } from "react";
import { api, setSession } from "@/lib/api";
import { Truck, ShieldCheck, User, ArrowLeft, Warehouse as WarehouseIcon } from "@phosphor-icons/react";

export default function AuthGate({ onSuccess }) {
  const [role, setRole] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [warehouse, setWarehouse] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload =
        role === "admin"
          ? { role, password }
          : { role, name: name.trim(), warehouse };
      const session = await api.login(payload);
      setSession(session);
      onSuccess(session);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-white placeholder-slate-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="auth-gate">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="bg-orange-500 text-white p-2 rounded-md">
            <Truck size={26} weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-white tracking-tight leading-none text-xl">
              FREIGHT KPI
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              Doral &amp; Medley
            </div>
          </div>
        </div>

        {!role && (
          <div data-testid="role-select">
            <p className="text-center text-slate-300 text-base md:text-lg mb-6">Who is signing in?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                data-testid="role-admin-btn"
                onClick={() => setRole("admin")}
                className="bg-slate-800 border border-slate-700 hover:border-orange-500 rounded-md p-6 text-left transition-colors duration-200"
              >
                <ShieldCheck size={32} weight="duotone" className="text-orange-400 mb-3" />
                <div className="font-heading text-xl font-bold text-white">Admin</div>
                <div className="text-sm text-slate-400 mt-1">Full access to both warehouses</div>
              </button>
              <button
                data-testid="role-manager-btn"
                onClick={() => setRole("manager")}
                className="bg-slate-800 border border-slate-700 hover:border-orange-500 rounded-md p-6 text-left transition-colors duration-200"
              >
                <User size={32} weight="duotone" className="text-orange-400 mb-3" />
                <div className="font-heading text-xl font-bold text-white">Manager</div>
                <div className="text-sm text-slate-400 mt-1">Daily entries for your warehouse</div>
              </button>
            </div>
          </div>
        )}

        {role === "admin" && (
          <form onSubmit={submit} className="bg-slate-800 border border-slate-700 rounded-md p-6" data-testid="admin-form">
            <div className="font-heading text-xl font-bold text-white mb-1">Admin sign in</div>
            <p className="text-sm text-slate-400 mb-4">Enter the admin password.</p>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              className={inputCls}
            />
            {error && (
              <p className="text-red-400 text-sm mt-2" data-testid="auth-error">{error}</p>
            )}
            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                data-testid="auth-back-btn"
                onClick={() => { setRole(null); setError(""); }}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors duration-200"
              >
                <ArrowLeft size={14} weight="bold" /> Back
              </button>
              <button
                data-testid="admin-login-btn"
                type="submit"
                disabled={busy || !password}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md font-semibold px-5 py-2 transition-colors duration-200"
              >
                {busy ? "Signing in..." : "Sign in"}
              </button>
            </div>
          </form>
        )}

        {role === "manager" && (
          <form onSubmit={submit} className="bg-slate-800 border border-slate-700 rounded-md p-6" data-testid="manager-form">
            <div className="font-heading text-xl font-bold text-white mb-1">Manager sign in</div>
            <p className="text-sm text-slate-400 mb-4">Enter your name and choose your warehouse location.</p>
            <input
              data-testid="manager-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoFocus
              className={inputCls}
            />
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[{ id: "doral", label: "Doral, FL" }, { id: "medley", label: "Medley, FL" }].map((w) => (
                <button
                  key={w.id}
                  type="button"
                  data-testid={`manager-warehouse-${w.id}`}
                  onClick={() => setWarehouse(w.id)}
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 font-semibold transition-colors duration-200 ${
                    warehouse === w.id
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "bg-slate-900 border-slate-600 text-slate-300 hover:border-slate-400"
                  }`}
                >
                  <WarehouseIcon size={18} weight="duotone" /> {w.label}
                </button>
              ))}
            </div>
            {error && (
              <p className="text-red-400 text-sm mt-2" data-testid="auth-error">{error}</p>
            )}
            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                data-testid="auth-back-btn"
                onClick={() => { setRole(null); setError(""); }}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors duration-200"
              >
                <ArrowLeft size={14} weight="bold" /> Back
              </button>
              <button
                data-testid="manager-login-btn"
                type="submit"
                disabled={busy || !name.trim() || !warehouse}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md font-semibold px-5 py-2 transition-colors duration-200"
              >
                {busy ? "Signing in..." : "Sign in"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
